import { Inject, Injectable } from '@angular/core';
import {
  getValue,
  InterpolationParameters,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  Translation,
  TranslateParser,
} from '@ngx-translate/core';
import { TRANSLATION_FALLBACK_LANGS } from './translation-fallback.tokens';

/**
 * Обработчик отсутствующих переводов, реализующий цепочку фолбэка.
 *
 * ngx-translate поддерживает только один язык фолбэка (`defaultLang`), поэтому требование
 * «показать русский, а если нет — английский» штатными средствами не покрывается.
 * Этот обработчик вызывается после того, как `defaultLang` не дал результата, и проходит
 * по цепочке {@link TRANSLATION_FALLBACK_LANGS}.
 *
 * Резолв синхронный и работает только по уже загруженным словарям: `instant()` и
 * `TranslatePipe` игнорируют Observable из обработчика. За предзагрузку цепочки отвечает
 * `TranslationFallbackService`.
 */
@Injectable()
export class FallbackChainMissingTranslationHandler implements MissingTranslationHandler {
  constructor(
    @Inject(TRANSLATION_FALLBACK_LANGS) private readonly _fallbackLangs: readonly string[],
    private readonly _parser: TranslateParser,
  ) {}

  /**
   * @returns перевод из первого языка цепочки, где найден ключ, либо `undefined`.
   * При `undefined` ngx-translate отдаёт сам ключ.
   */
  handle(params: MissingTranslationHandlerParams): Translation {
    const { key, translateService, interpolateParams } = params;

    for (const lang of this._fallbackLangs) {
      if (lang === translateService.currentLang) {
        continue;
      }

      const translations = translateService.translations[lang];
      if (!translations) {
        continue;
      }

      const value = getValue(translations, key);
      if (value === undefined) {
        continue;
      }

      const result = this._interpolate(value, interpolateParams);
      if (result !== undefined) {
        return result;
      }
    }

    return undefined;
  }

  /**
   * Повторяет логику интерполяции ngx-translate: словари скомпилированы в функции
   * messageformat, а ключ может указывать не на строку, а на поддерево или массив.
   */
  private _interpolate(value: unknown, interpolateParams?: InterpolationParameters): Translation {
    if (Array.isArray(value)) {
      return value.map((item) => this._interpolate(item, interpolateParams));
    }

    if (value !== null && typeof value === 'object') {
      const result: Record<string, Translation> = {};
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        const nestedResult = this._interpolate(nestedValue, interpolateParams);
        if (nestedResult !== undefined) {
          result[nestedKey] = nestedResult;
        }
      }
      return result;
    }

    return this._parser.interpolate(value as string, interpolateParams);
  }
}
